// File: Customers.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Switch,
  FormControlLabel,
  Tooltip,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Extension as ExtensionIcon,
  LocalPhone as LocalPhoneIcon,
  Inventory as PackageIcon,
  SupervisorAccount as SupervisorIcon,
  Router as RouterIcon,
  Wifi as WifiIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useCustomerFilterContext } from '../../contexts/CustomerFilterContext';
import api from '../../services/api';
// customerViewService import removed - using sessionStorage directly
import { useNavigate } from 'react-router-dom';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('Customers');

interface Customer {
  id: number;
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  // Billing Address
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  // Additional Contact Information
  fax?: string;
  alternatePhone?: string;
  website?: string;
  // Billing Information
  taxId?: string;
  paymentTerms?: string;
  creditLimit?: number;
  // System fields
  status: 'Active' | 'Disabled';
  memberCount: number;
  extensionCount: number;
  didCount: number;
  packageName: string;
  monthlyPrice: number;
  totalMonthlyRate: number;
  managedBy: string;
  underCustId?: number;
  createdAt?: string;
}

interface Package {
  id?: number;
  PackageID?: number;
  name?: string;
  PackageName?: string;
  monthlyPrice?: number;
  MonthlyPrice?: number;
  packageType?: string;
  PackageType?: string;
}

const Customers: React.FC = () => {
  const { member } = useAuth();
  const navigate = useNavigate();
  const { setCustomerFilter } = useCustomerFilterContext();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'Active' | 'Disabled'>('all');
  const [partnerFilter, setPartnerFilter] = useState<number | 'all'>('all');
  const [partners, setPartners] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const [formData, setFormData] = useState({
    // Basic Information
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Billing Address
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
    // Additional Contact Information
    fax: '',
    alternatePhone: '',
    website: '',
    // Billing Information
    taxId: '',
    paymentTerms: 'Net 30',
    creditLimit: 0,
    // System
    status: 'Active',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [packageDialog, setPackageDialog] = useState(false);
  const [customerPackages, setCustomerPackages] = useState<any[]>([]);
  const [customerServices, setCustomerServices] = useState<any>({ phone: [], internet: [], addons: [] });
  const [serviceTotals, setServiceTotals] = useState<any>({ monthlyTotal: 0, phoneServices: 0, internetServices: 0, addonServices: 0 });
  const [serviceTab, setServiceTab] = useState<'phone' | 'internet' | 'addons'>('phone');
  const [serviceStatusFilter, setServiceStatusFilter] = useState<'all' | 'active' | 'cancelled'>('active');
  const [packageReplaceConfirm, setPackageReplaceConfirm] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [addPackageDialog, setAddPackageDialog] = useState(false);
  const [editPackageDialog, setEditPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState<any>(null);
  const [packageType, setPackageType] = useState<'voip' | 'internet' | 'addon' | ''>('');
  const [packageCategory, setPackageCategory] = useState<'residential' | 'business' | ''>('');
  const [addPackageStep, setAddPackageStep] = useState(1);
  const [editFormData, setEditFormData] = useState<any>({});
  const [selectedServices, setSelectedServices] = useState<{
    pbx: any | null;
    internet: any | null;
    dualInternet: any | null;
    addons: any[];
    location?: string;
    newLocationName?: string;
  }>({ pbx: null, internet: null, dualInternet: null, addons: [], location: 'main' });
  const [internetServices, setInternetServices] = useState<any[]>([]);
  const [dualInternetServices, setDualInternetServices] = useState<any[]>([]);
  const [addonServices, setAddonServices] = useState<any[]>([]);
  const [customerLocations, setCustomerLocations] = useState<string[]>(['main']);
  
  // Custom rate dialog states
  const [customRateDialog, setCustomRateDialog] = useState(false);
  const [customRateType, setCustomRateType] = useState<'service' | 'internet' | 'addon'>('service');
  const [customRateForm, setCustomRateForm] = useState({
    name: '',
    description: '',
    price: '',
    quantity: '1',
    promoPrice: '',
    promoEndDate: '',
    billDate: new Date().toISOString().split('T')[0],
  });
  const [editDialogTab, setEditDialogTab] = useState(0);
  const [editCustomerPackages, setEditCustomerPackages] = useState<any[]>([]);

  const [summary, setSummary] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    totalExtensions: 0,
    totalNumbers: 0,
    totalRevenue: 0,
  });

  useEffect(() => {
    fetchCustomers();
    fetchPartners();
    // Don't load packages and services until needed
  }, []);
  
  useEffect(() => {
    // Load all available packages when package dialog opens
    if (packageDialog && packages.length === 0) {
      fetchPackages();
    }
  }, [packageDialog]);
  
  useEffect(() => {
    // Load services when add package dialog opens
    if (addPackageDialog && selectedCustomer) {
      logger.debug('Add package dialog opened, loading services...');
      fetchPackages();
      fetchInternetServices();
      fetchDualInternetServices();
      fetchAddonServices();
      // Fetch existing locations for this customer
      fetchCustomerLocations();
    }
  }, [addPackageDialog, selectedCustomer?.id]);

  // Set document title
  useEffect(() => {
    document.title = 'Customer Management - Portal';
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/customers');
      logger.debug('Customers API response:', response);
      if (response.data.success) {
        setCustomers(response.data.data);
        calculateSummary(response.data.data);
      }
    } catch (error: any) {
      logger.error('Error fetching customers:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch customers';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await api.get('/endpoints/partners');
      if (response.data.success) {
        setPartners(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching partners:', error);
      setPartners([]);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get('/endpoints/packages');
      logger.debug('Packages API response:', response.data);
      if (response.data.success && response.data.data?.packages) {
        const packagesData = response.data.data.packages;
        logger.debug('Packages data:', packagesData);
        // Preserve the original API structure from the packages endpoint
        setPackages(packagesData);
      } else {
        setPackages([]);
      }
    } catch (error) {
      logger.error('Error fetching packages:', error);
      setPackages([]); // Ensure packages is always an array
    }
  };

  const fetchInternetServices = async () => {
    try {
      const response = await api.get('/endpoints/internetservices');
      logger.debug('Internet services API response:', response.data);
      if (response.data.success) {
        // The data is returned directly in response.data.data (array)
        const services = response.data.data || [];
        
        // Show all active services (both retail and wholesale for now)
        const activeServices = services.filter((service: any) => service.is_active);
        
        logger.debug('All services:', services);
        logger.debug('Active services:', activeServices);
        setInternetServices(activeServices);
      }
    } catch (error) {
      logger.error('Error fetching internet services:', error);
      setInternetServices([]);
    }
  };

  const fetchDualInternetServices = async () => {
    try {
      const response = await api.get('/endpoints/dual-internet-services');
      logger.debug('Dual internet services API response:', response.data);
      if (response.data.success) {
        const services = response.data.data || [];
        
        // Sort by Cable speed order, then by secondary type and speed
        const cableSpeedOrder = ['30M', '75M', '150M', '300M', '500M', '1G'];
        const secondaryTypeOrder = ['VDSL', 'Fibre'];
        
        const sortedServices = services.sort((a: any, b: any) => {
          // First, ensure Cable is primary (should already be the case)
          if (a.primary_type !== 'Cable' || b.primary_type !== 'Cable') {
            return a.primary_type === 'Cable' ? -1 : 1;
          }
          
          // Get Cable speed order
          const aSpeedIndex = cableSpeedOrder.indexOf(a.primary_speed || a.primary_download?.replace(' Mbps', '').replace(' Gbps', 'G'));
          const bSpeedIndex = cableSpeedOrder.indexOf(b.primary_speed || b.primary_download?.replace(' Mbps', '').replace(' Gbps', 'G'));
          
          if (aSpeedIndex !== bSpeedIndex) {
            return aSpeedIndex - bSpeedIndex;
          }
          
          // Same Cable speed, sort by secondary type (VDSL before Fibre)
          const aTypeIndex = secondaryTypeOrder.indexOf(a.secondary_type);
          const bTypeIndex = secondaryTypeOrder.indexOf(b.secondary_type);
          
          if (aTypeIndex !== bTypeIndex) {
            return aTypeIndex - bTypeIndex;
          }
          
          // Same secondary type, sort by secondary speed
          const aSecondarySpeed = parseInt(a.secondary_download?.replace(/[^\d]/g, '') || '0');
          const bSecondarySpeed = parseInt(b.secondary_download?.replace(/[^\d]/g, '') || '0');
          
          return aSecondarySpeed - bSecondarySpeed;
        });
        
        setDualInternetServices(sortedServices);
      }
    } catch (error) {
      logger.error('Error fetching dual internet services:', error);
      setDualInternetServices([]);
    }
  };

  const fetchAddonServices = async () => {
    try {
      const response = await api.get('/endpoints/addonservices');
      logger.debug('Addon services response:', response.data);
      
      if (response.data.success && response.data.data) {
        // The API returns data.services array
        const services = response.data.data.services || [];
        setAddonServices(services);
        logger.debug('Addon services loaded:', services);
      }
    } catch (error) {
      logger.error('Error fetching add-on services:', error);
      setAddonServices([]);
    }
  };

  const fetchCustomerLocations = async () => {
    try {
      if (!selectedCustomer) return;
      
      const response = await api.get(`/endpoints/customer-services/${selectedCustomer.id}/all`);
      if (response.data.success) {
        const services = response.data.data.services;
        const locations = new Set<string>(['main']);
        
        // Extract unique locations from all services
        if (services.phone) {
          services.phone.forEach((s: any) => {
            if (s.locationName) locations.add(s.locationName);
          });
        }
        if (services.internet) {
          services.internet.forEach((s: any) => {
            if (s.locationName) locations.add(s.locationName);
          });
        }
        
        setCustomerLocations(Array.from(locations));
      }
    } catch (error) {
      logger.error('Error fetching customer locations:', error);
    }
  };

  const calculateSummary = (customerData: Customer[]) => {
    const summary = customerData.reduce(
      (acc, customer) => {
        acc.totalCustomers++;
        if (customer.status === 'Active') {
          acc.activeCustomers++;
        }
        acc.totalExtensions += customer.extensionCount || 0;
        acc.totalNumbers += customer.didCount || 0;
        acc.totalRevenue += customer.totalMonthlyRate || 0;
        return acc;
      },
      {
        totalCustomers: 0,
        activeCustomers: 0,
        totalExtensions: 0,
        totalNumbers: 0,
        totalRevenue: 0,
      }
    );
    setSummary(summary);
  };

  const handleOpenDialog = async (customer?: Customer) => {
    setEditDialogTab(0); // Reset to first tab
    setEditCustomerPackages([]); // Clear previous packages

    if (customer) {
      setSelectedCustomer(customer);
      setFormData({
        // Basic Information
        companyName: customer.companyName || '',
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email,
        phone: customer.phone || '',
        // Billing Address
        address: customer.address || '',
        city: customer.city || '',
        province: customer.province || '',
        postalCode: customer.postalCode || '',
        country: customer.country || 'Canada',
        // Additional Contact Information
        fax: customer.fax || '',
        alternatePhone: customer.alternatePhone || '',
        website: customer.website || '',
        // Billing Information
        taxId: customer.taxId || '',
        paymentTerms: customer.paymentTerms || 'Net 30',
        creditLimit: customer.creditLimit || 0,
        // System
        status: customer.status,
      });

      // Load customer packages for the Packages tab
      try {
        const packagesRes = await api.get(`/endpoints/customers/${customer.id}/packages`);
        setEditCustomerPackages(packagesRes.data.data || []);
      } catch (error) {
        console.error('Failed to load customer packages:', error);
        setEditCustomerPackages([]);
      }
    } else {
      setSelectedCustomer(null);
      setFormData({
        // Basic Information
        companyName: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        // Billing Address
        address: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Canada',
        // Additional Contact Information
        fax: '',
        alternatePhone: '',
        website: '',
        // Billing Information
        taxId: '',
        paymentTerms: 'Net 30',
        creditLimit: 0,
        // System
        status: 'Active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
    setFormData({
      // Basic Information
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      // Billing Address
      address: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Canada',
      // Additional Contact Information
      fax: '',
      alternatePhone: '',
      website: '',
      // Billing Information
      taxId: '',
      paymentTerms: 'Net 30',
      creditLimit: 0,
      // System
      status: 'Active',
    });
  };

  const handleSubmit = async () => {
    try {
      if (selectedCustomer) {
        // Update existing customer
        const response = await api.put(`/endpoints/customers/${selectedCustomer.id}`, formData);
        if (response.data.success) {
          setSnackbar({ open: true, message: 'Customer updated successfully', severity: 'success' });
          fetchCustomers();
          handleCloseDialog();
        }
      } else {
        // Create new customer
        const response = await api.post('/endpoints/customers', formData);
        if (response.data.success) {
          setSnackbar({ open: true, message: 'Customer created successfully', severity: 'success' });
          fetchCustomers();
          handleCloseDialog();
        }
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Operation failed', 
        severity: 'error' 
      });
    }
  };

  const handleManagePackages = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setPackageDialog(true);
    setLoadingPackages(true);
    
    // Clear previous data to avoid showing stale information
    setCustomerServices({ phone: [], internet: [], addons: [] });
    setServiceTotals({ monthlyTotal: 0, phoneServices: 0, internetServices: 0, addonServices: 0 });
    setCustomerPackages([]);
    
    try {
      // Try to load all services from the enhanced endpoint
      try {
        const servicesRes = await api.get(`/endpoints/customer-services/${customer.id}/all`);
        const data = servicesRes.data.data;
        
        // Debug logging
        logger.debug('Services response:', data);
        logger.debug('Internet services:', data.services?.internet);
        logger.debug('Addon services:', data.services?.addons);
        logger.debug('Service totals:', data.totals);
        logger.debug('Internet totals specifically:', data.totals?.internetServices);
        logger.debug('Addon totals specifically:', data.totals?.addonServices);
        
        // Set all services
        setCustomerServices(data.services);
        setServiceTotals(data.totals);
        
        // For backward compatibility, set phone packages
        setCustomerPackages(data.services.phone || []);
      } catch (enhancedError: any) {
        logger.debug('Enhanced endpoint not available, falling back to basic packages', enhancedError);
        logger.debug('Error response:', enhancedError.response?.data);
        
        // Fallback to old endpoint
        const packagesRes = await api.get(`/endpoints/customers/${customer.id}/packages`);
        const packages = packagesRes.data.data || [];
        setCustomerPackages(packages);
        
        // Calculate totals from packages
        const phoneTotal = packages.reduce((sum: number, pkg: any) => {
          if (pkg.active) {
            return sum + (pkg.monthlyPrice || 0);
          }
          return sum;
        }, 0);
        
        // Set enhanced data with calculated totals
        setCustomerServices({ phone: packages, internet: [], addons: [] });
        setServiceTotals({ 
          monthlyTotal: phoneTotal, 
          phoneServices: phoneTotal, 
          internetServices: 0, 
          addonServices: 0 
        });
      }
    } catch (error: any) {
      logger.error('Failed to load customer services:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load customer services', 
        severity: 'error' 
      });
    } finally {
      setLoadingPackages(false);
    }
  };
  
  const handleAddPackage = async (packageId: number) => {
    if (!selectedCustomer) return;
    
    try {
      const response = await api.post(`/endpoints/customers/${selectedCustomer.id}/packages`, {
        packageId
      });
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Package added successfully', severity: 'success' });
        // Reload customer packages
        handleManagePackages(selectedCustomer);
        // Reload main customer list to update totals
        fetchCustomers();
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to add package', 
        severity: 'error' 
      });
    }
  };
  
  const handleEditPackage = (pkg: any) => {
    setEditingPackage(pkg);
    // Initialize edit form with current package data
    setEditFormData({
      monthlyPrice: pkg.monthlyPrice || 0,
      setupFee: pkg.setupFee || 0,
      notes: pkg.notes || '',
      active: pkg.active !== false,
    });
    setEditPackageDialog(true);
  };
  
  const handleSelectPackage = async (pkg: Package) => {
    if (!selectedCustomer) return;
    
    const packageId = pkg.id || pkg.PackageID;
    if (!packageId) {
      logger.error('Package ID not found');
      return;
    }
    
    try {
      await handleAddPackage(packageId);
      setAddPackageDialog(false);
    } catch (error) {
      logger.error('Error adding package:', error);
    }
  };

  const handleEndPackage = async (pkg: any) => {
    if (!selectedCustomer) return;
    
    const endDate = window.prompt('Enter end date for this package (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    
    if (!endDate) return;
    
    try {
      const response = await api.put(`/endpoints/customers/${selectedCustomer.id}/packages/${pkg.id}`, {
        endDate: endDate,
        status: 'cancelled'
      });
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Package end date set successfully', severity: 'success' });
        // Reload customer packages
        handleManagePackages(selectedCustomer);
        // Reload main customer list to update totals
        fetchCustomers();
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to set end date', 
        severity: 'error' 
      });
    }
  };

  const handleDelete = async (customerId: number) => {
    console.log('[DELETE] handleDelete called with customerId:', customerId);
    try {
      console.log('[DELETE] Making first API request...');
      // First request - get resource information
      const response = await api.delete(`/endpoints/customers?id=${customerId}`, {
        data: { confirm: false }
      });

      console.log('[DELETE] First response:', response.data);

      // Check if requiresConfirmation is at data.data or just data level
      const confirmationData = response.data.data || response.data;
      console.log('[DELETE] Confirmation data:', confirmationData);
      console.log('[DELETE] requiresConfirmation?', confirmationData.requiresConfirmation);

      if (confirmationData.requiresConfirmation) {
        console.log('[DELETE] Entering first confirmation block');
        const resourceInfo = confirmationData;
        const resources = resourceInfo.resourcesAffected;
        
        // Build resource list only showing non-zero counts
        let resourceList = [];
        if (resources.extensions > 0) resourceList.push(`• ${resources.extensions} Extensions (will be deleted)`);
        if (resources.softphones > 0) resourceList.push(`• ${resources.softphones} Softphones (will be deleted)`);
        if (resources.inboundNumbers > 0) resourceList.push(`• ${resources.inboundNumbers} Inbound Phone Numbers (will be deactivated)`);
        if (resources.outboundNumbers > 0) resourceList.push(`• ${resources.outboundNumbers} Outbound Phone Numbers (will be deleted)`);
        if (resources.users > 0) resourceList.push(`• ${resources.users} Users (will be deleted)`);
        if (resources.timeConditions > 0) resourceList.push(`• ${resources.timeConditions} Time Conditions`);
        if (resources.ivrMenus > 0) resourceList.push(`• ${resources.ivrMenus} IVR Menus`);
        if (resources.queues > 0) resourceList.push(`• ${resources.queues} Call Queues`);
        if (resources.ringGroups > 0) resourceList.push(`• ${resources.ringGroups} Ring Groups`);
        if (resources.prompts > 0) resourceList.push(`• ${resources.prompts} Voice Prompts`);
        if (resources.autoAttendants > 0) resourceList.push(`• ${resources.autoAttendants} Auto Attendants`);
        if (resources.conferenceBridges > 0) resourceList.push(`• ${resources.conferenceBridges} Conference Bridges`);
        if (resources.activeBillingRates > 0) resourceList.push(`• ${resources.activeBillingRates} Active Billing Rates`);
        
        // Show first confirmation dialog
        const firstConfirm = window.confirm(
          `Delete Customer: ${resourceInfo.customer.name || resourceInfo.customer.email}\n\n` +
          `This will permanently delete:\n` +
          (resourceList.length > 0 ? resourceList.join('\n') : '• No active resources') + `\n\n` +
          `${resourceInfo.message}\n\n` +
          `Do you want to proceed?`
        );

        console.log('[DELETE] User clicked:', firstConfirm ? 'OK' : 'Cancel');
        if (!firstConfirm) return;
        
        // Second request - get final warning
        const secondResponse = await api.delete(`/endpoints/customers?id=${customerId}`, {
          data: { confirm: true }
        });

        console.log('Second delete response:', secondResponse.data);
        const secondConfirmationData = secondResponse.data.data || secondResponse.data;

        if (secondConfirmationData.requiresFinalConfirmation) {
          console.log('[DELETE] Showing final confirmation');
          // Final confirmation using confirm dialog (prompt() doesn't work reliably)
          const finalConfirm = window.confirm(
            `⚠️ FINAL WARNING ⚠️\n\n` +
            `${secondConfirmationData.warningMessage}\n\n` +
            `Click OK to permanently delete this customer and all resources.\n` +
            `This action CANNOT be undone!`
          );

          if (!finalConfirm) {
            setSnackbar({
              open: true,
              message: 'Customer deletion cancelled',
              severity: 'error'
            });
            return;
          }

          console.log('[DELETE] Proceeding with final delete API call');
          // Final delete request
          const finalResponse = await api.delete(`/endpoints/customers?id=${customerId}`, {
            data: { confirm: true, finalConfirm: true }
          });

          console.log('[DELETE] Final delete response:', finalResponse.data);

          if (finalResponse.data.success) {
            const responseData = finalResponse.data.data || finalResponse.data;
            const deleted = responseData.deletedResources || {};
            setSnackbar({
              open: true,
              message: `Customer deactivated successfully. Deleted: ${deleted.extensions || 0} extensions, ${deleted.softphones || 0} softphones, ${deleted.users || 0} users. ${deleted.phoneNumbers || 0} phone numbers deactivated (recoverable for 20 days).`,
              severity: 'success'
            });
            fetchCustomers();
          }
        }
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to delete customer', 
        severity: 'error' 
      });
    }
  };

  const handleCancelInternetService = async (service: any) => {
    if (!selectedCustomer) return;
    
    const endDate = window.prompt(
      `Enter billing end date for this internet service (YYYY-MM-DD):\n\n${service.serviceName}\nMonthly Rate: $${service.monthlyRate}\n\nLeave empty to end immediately:`,
      new Date().toISOString().split('T')[0]
    );
    
    if (endDate === null) return; // User cancelled
    
    try {
      const serviceId = typeof service.id === 'string' && service.id.startsWith('dual_') 
        ? service.id.replace('dual_', '') 
        : service.id;
      
      const endpoint = service.isDualInternet 
        ? `/endpoints/customer-services/${selectedCustomer.id}/dual-internet/${serviceId}`
        : `/endpoints/customer-services/${selectedCustomer.id}/internet/${serviceId}`;
      
      const response = await api.put(endpoint, {
        status: 'cancelled',
        billingEndDate: endDate || new Date().toISOString().split('T')[0]
      });
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Internet service cancelled successfully', severity: 'success' });
        handleManagePackages(selectedCustomer);
        fetchCustomers();
      }
    } catch (error: any) {
      logger.error('Error cancelling internet service:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to cancel internet service', 
        severity: 'error' 
      });
    }
  };

  const handleRemoveAddon = async (addon: any) => {
    if (!selectedCustomer) return;
    
    const confirm = window.confirm(`Are you sure you want to remove this add-on?\n\n${addon.addonName}\nQuantity: ${addon.quantity}\nTotal: $${addon.totalPrice}/${addon.billingCycle}`);
    
    if (!confirm) return;
    
    try {
      const response = await api.delete(`/endpoints/customer-services/${selectedCustomer.id}/addons/${addon.id}`);
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Add-on removed successfully', severity: 'success' });
        handleManagePackages(selectedCustomer);
        fetchCustomers();
      }
    } catch (error: any) {
      logger.error('Error removing addon:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to remove add-on', 
        severity: 'error' 
      });
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    
    const matchesPartner = partnerFilter === 'all' || customer.underCustId === partnerFilter;
    
    return matchesSearch && matchesStatus && matchesPartner;
  });

  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Helper function to count filtered services
  const getFilteredServiceCount = (services: any[], type: 'phone' | 'internet' | 'addons') => {
    if (!services) return 0;
    
    if (type === 'phone') {
      return services.filter((pkg: any) => {
        if (serviceStatusFilter === 'active') return pkg.active && (!pkg.endDate || new Date(pkg.endDate) > new Date());
        if (serviceStatusFilter === 'cancelled') return !pkg.active || (pkg.endDate && new Date(pkg.endDate) <= new Date());
        return true;
      }).length;
    } else {
      return services.filter((service: any) => {
        if (serviceStatusFilter === 'active') return service.status === 'active';
        if (serviceStatusFilter === 'cancelled') return service.status !== 'active';
        return true;
      }).length;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Customer Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCustomers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Customers
                  </Typography>
                  <Typography variant="h5">{summary.totalCustomers}</Typography>
                </Box>
                <BusinessIcon color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Active Customers
                  </Typography>
                  <Typography variant="h5">{summary.activeCustomers}</Typography>
                </Box>
                <PeopleIcon color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Extensions
                  </Typography>
                  <Typography variant="h5">{summary.totalExtensions}</Typography>
                </Box>
                <ExtensionIcon color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Numbers
                  </Typography>
                  <Typography variant="h5">{summary.totalNumbers}</Typography>
                </Box>
                <LocalPhoneIcon color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Monthly Revenue
                  </Typography>
                  <Typography variant="h5">${summary.totalRevenue.toFixed(2)}</Typography>
                </Box>
                <MoneyIcon color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Partner</InputLabel>
              <Select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value as number | 'all')}
                label="Partner"
              >
                <MenuItem value="all">All Partners</MenuItem>
                {partners.map((partner) => (
                  <MenuItem key={partner.cust_id} value={partner.cust_id}>
                    {partner.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'Active' | 'Disabled')}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell align="center">Members</TableCell>
              <TableCell align="center">Extensions</TableCell>
              <TableCell align="center">Numbers</TableCell>
              <TableCell>Package</TableCell>
              <TableCell align="right">Monthly</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Managed By</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCustomers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell>{customer.id}</TableCell>
                <TableCell>
                  {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                </TableCell>
                <TableCell>{customer.email}</TableCell>
                <TableCell>{customer.phone || '-'}</TableCell>
                <TableCell align="center">
                  <Chip
                    icon={<PeopleIcon />}
                    label={customer.memberCount}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    icon={<ExtensionIcon />}
                    label={customer.extensionCount}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    icon={<PhoneIcon />}
                    label={customer.didCount}
                    size="small"
                  />
                </TableCell>
                <TableCell>{customer.packageName}</TableCell>
                <TableCell align="right">${customer.totalMonthlyRate.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip
                    label={customer.status}
                    color={customer.status === 'Active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{customer.managedBy}</TableCell>
                <TableCell align="center">
                  {(member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin') && (
                    <Tooltip title="Login as Customer">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          const customerName = customer.companyName || `${customer.firstName} ${customer.lastName}`;
                          logger.debug('Login as customer clicked:', { id: customer.id, name: customerName });
                          alert(`Login as Customer clicked for: ${customer.id} - ${customerName}`);
                          
                          try {
                            // Store intent on server and get secure token
                            logger.debug('Sending POST to /endpoints/login_as_customer with:', {
                              customerId: customer.id,
                              customerName: customerName
                            });
                            const response = await api.post('/endpoints/login_as_customer', {
                              customerId: customer.id,
                              customerName: customerName
                            });
                            
                            logger.debug('Server response:', response);
                            logger.debug('Response data:', response.data);
                            logger.debug('Response data.data:', response.data?.data);
                            
                            if (response.data.success && response.data.data?.token) {
                              const token = response.data.data.token;
                              const url = `/extensions?lat=${token}`;
                              logger.debug('Opening new tab with URL:', url);
                              logger.debug('Token received:', token);
                              // Open new tab with only the secure token
                              window.open(url, '_blank');
                            } else {
                              logger.error('Failed to create login-as token');
                              logger.error('Response was:', response.data);
                              alert('Failed to create login token. Check console for details.');
                            }
                          } catch (error: any) {
                            logger.error('Error creating login-as session:', error);
                            logger.error('Error response:', error.response?.data);
                            logger.error('Error status:', error.response?.status);
                            alert(`Error creating login session: ${error.response?.data?.message || error.message}`);
                          }
                        }}
                        sx={{ color: '#9c27b0' }}
                      >
                        <LoginIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Members">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCustomerFilter(String(customer.id), customer.companyName || `${customer.firstName} ${customer.lastName}`);
                        navigate('/users');
                      }}
                      color="primary"
                    >
                      <PeopleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Extensions">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCustomerFilter(String(customer.id), customer.companyName || `${customer.firstName} ${customer.lastName}`);
                        navigate('/extensions');
                      }}
                      color="info"
                    >
                      <ExtensionIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Phone Numbers">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCustomerFilter(String(customer.id), customer.companyName || `${customer.firstName} ${customer.lastName}`);
                        navigate('/phone-numbers');
                      }}
                      color="warning"
                    >
                      <LocalPhoneIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Billing">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCustomerFilter(String(customer.id), customer.companyName || `${customer.firstName} ${customer.lastName}`);
                        navigate('/billing');
                      }}
                      color="success"
                    >
                      <MoneyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Manage Packages">
                    <IconButton
                      size="small"
                      onClick={() => handleManagePackages(customer)}
                      color="secondary"
                    >
                      <PackageIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(customer)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => {
                        console.log('[CLICK] Delete button clicked for customer ID:', customer.id);
                        handleDelete(customer.id);
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredCustomers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <Tabs value={editDialogTab} onChange={(e, newValue) => setEditDialogTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="General Information" />
              <Tab label="Packages & Billing" />
              <Tab label="Services & DIDs" />
              <Tab label="Activity" />
            </Tabs>
          )}

          {/* Tab Panel 0: General Information */}
          {(!selectedCustomer || editDialogTab === 0) && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Basic Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                  Basic Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Disabled' })}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Disabled">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://"
                />
              </Grid>

              {/* Contact Information Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                  Contact Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Primary Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Alternate Phone"
                  value={formData.alternatePhone}
                  onChange={(e) => setFormData({ ...formData, alternatePhone: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Fax"
                  value={formData.fax}
                  onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                />
              </Grid>

              {/* Billing Address Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                  Billing Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address, suite, unit, etc."
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Province/State"
                  value={formData.province}
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </Grid>

              {/* Billing Information Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                  Billing Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Tax ID / Business Number"
                  value={formData.taxId}
                  onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                  placeholder="Optional"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Payment Terms</InputLabel>
                  <Select
                    value={formData.paymentTerms}
                    onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                    label="Payment Terms"
                  >
                    <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                    <MenuItem value="Net 15">Net 15 Days</MenuItem>
                    <MenuItem value="Net 30">Net 30 Days</MenuItem>
                    <MenuItem value="Net 45">Net 45 Days</MenuItem>
                    <MenuItem value="Net 60">Net 60 Days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Credit Limit"
                  type="number"
                  value={formData.creditLimit}
                  onChange={(e) => setFormData({ ...formData, creditLimit: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Service Rates Section - This is where we'll add package management */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                  Service Rates & Packages
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<PhoneIcon />}
                    onClick={() => {
                      setCustomRateDialog(true);
                      setCustomRateType('service');
                    }}
                    color="primary"
                    disabled={!selectedCustomer}
                  >
                    Add Service Rate
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<WifiIcon />}
                    onClick={() => {
                      setCustomRateDialog(true);
                      setCustomRateType('internet');
                    }}
                    color="success"
                    disabled={!selectedCustomer}
                  >
                    Add Internet Rate
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setCustomRateDialog(true);
                      setCustomRateType('addon');
                    }}
                    color="warning"
                    disabled={!selectedCustomer}
                  >
                    Add Addon Rate
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </Box>
          )}

          {/* Tab Panel 1: Packages & Billing */}
          {selectedCustomer && editDialogTab === 1 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Package Configuration</Typography>
                <Button
                  variant="contained"
                  startIcon={<PackageIcon />}
                  onClick={() => handleManagePackages(selectedCustomer)}
                  size="small"
                >
                  Manage Services
                </Button>
              </Box>

              {editCustomerPackages.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Package Name</TableCell>
                          <TableCell>Billing Type</TableCell>
                          <TableCell>Monthly Price</TableCell>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editCustomerPackages.map((pkg: any) => (
                          <TableRow key={pkg.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {pkg.packageName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pkg.billingCycle || 'Monthly'} billing
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={pkg.billingType === 'postpaid' ? 'Postpaid' : 'Prepaid'}
                                color={pkg.billingType === 'postpaid' ? 'warning' : 'primary'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                ${pkg.monthlyPrice?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {pkg.billingType === 'postpaid' ? (
                                <Typography variant="body2">
                                  NET {pkg.paymentTerms || 30}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Prepaid
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={pkg.active ? 'Active' : 'Inactive'}
                                color={pkg.active ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Change Billing Type">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    const newType = pkg.billingType === 'postpaid' ? 'prepaid' : 'postpaid';
                                    let paymentTerms = pkg.paymentTerms;

                                    if (newType === 'postpaid') {
                                      const input = prompt('Enter payment terms (days):', '30');
                                      if (!input) return;
                                      paymentTerms = parseInt(input);
                                    }

                                    try {
                                      await api.put(`/endpoints/customers/${selectedCustomer.id}/packages/${pkg.id}`, {
                                        billingType: newType,
                                        paymentTerms: paymentTerms
                                      });

                                      // Reload packages
                                      const packagesRes = await api.get(`/endpoints/customers/${selectedCustomer.id}/packages`);
                                      setEditCustomerPackages(packagesRes.data.data || []);

                                      setSnackbar({
                                        open: true,
                                        message: `Package billing type updated to ${newType}`,
                                        severity: 'success'
                                      });
                                    } catch (error) {
                                      setSnackbar({
                                        open: true,
                                        message: 'Failed to update billing type',
                                        severity: 'error'
                                      });
                                    }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Total Monthly Revenue
                          </Typography>
                          <Typography variant="h5">
                            ${editCustomerPackages.reduce((sum: number, pkg: any) =>
                              pkg.active ? sum + (pkg.monthlyPrice || 0) : sum, 0).toFixed(2)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Billing Mode
                          </Typography>
                          <Typography variant="h6">
                            {editCustomerPackages.every((pkg: any) => pkg.billingType === 'postpaid') ? 'Postpaid' :
                             editCustomerPackages.every((pkg: any) => pkg.billingType !== 'postpaid') ? 'Prepaid' : 'Mixed'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Active Packages
                          </Typography>
                          <Typography variant="h5">
                            {editCustomerPackages.filter((pkg: any) => pkg.active).length} / {editCustomerPackages.length}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  No packages configured. Click "Manage Services" to add packages.
                </Alert>
              )}
            </Box>
          )}

          {/* Tab Panel 2: Services & DIDs */}
          {selectedCustomer && editDialogTab === 2 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Services & Phone Numbers</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Click the "Manage Services" button in the Packages tab to configure services and DIDs.
              </Alert>
            </Box>
          )}

          {/* Tab Panel 3: Activity */}
          {selectedCustomer && editDialogTab === 3 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Customer Activity</Typography>
              <Alert severity="info">
                Activity history and audit logs will be displayed here.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedCustomer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity as 'success' | 'error' | 'warning' | 'info'} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Package Management Dialog */}
      <Dialog open={packageDialog} onClose={() => setPackageDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Manage Packages - {selectedCustomer?.companyName || selectedCustomer?.email}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddPackageDialog(true)}
                size="small"
                sx={{ minWidth: 120 }}
              >
                Add Package
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhoneIcon />}
                onClick={() => {
                  setCustomRateDialog(true);
                  setCustomRateType('service');
                }}
                size="small"
                color="primary"
                sx={{ minWidth: 140 }}
              >
                Add Service Rate
              </Button>
              <Button
                variant="outlined"
                startIcon={<WifiIcon />}
                onClick={() => {
                  setCustomRateDialog(true);
                  setCustomRateType('internet');
                }}
                size="small"
                color="success"
                sx={{ minWidth: 140 }}
              >
                Add Internet Rate
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCustomRateDialog(true);
                  setCustomRateType('addon');
                }}
                size="small"
                color="warning"
                sx={{ minWidth: 130 }}
              >
                Add Addon Rate
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingPackages ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Status Filter */}
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={serviceStatusFilter}
                  exclusive
                  onChange={(e, newValue) => newValue && setServiceStatusFilter(newValue)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="active">Active Services</ToggleButton>
                  <ToggleButton value="all">All Services</ToggleButton>
                  <ToggleButton value="cancelled">Cancelled Services</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Service Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">Phone Services</Typography>
                    <Typography variant="h6">${serviceTotals.phoneServices.toFixed(2)}/mo</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">Internet Services</Typography>
                    <Typography variant="h6">${serviceTotals.internetServices.toFixed(2)}/mo</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">Add-on Services</Typography>
                    <Typography variant="h6">${serviceTotals.addonServices.toFixed(2)}/mo</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">Total Monthly</Typography>
                    <Typography variant="h5" color="primary">${serviceTotals.monthlyTotal.toFixed(2)}/mo</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Service Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Button
                  sx={{
                    borderBottom: serviceTab === 'phone' ? 2 : 0,
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    color: serviceTab === 'phone' ? 'primary.main' : 'text.secondary',
                    fontWeight: serviceTab === 'phone' ? 'bold' : 'normal',
                  }}
                  onClick={() => setServiceTab('phone')}
                >
                  Phone Services ({getFilteredServiceCount(customerServices.phone, 'phone')})
                </Button>
                <Button
                  sx={{
                    borderBottom: serviceTab === 'internet' ? 2 : 0,
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    color: serviceTab === 'internet' ? 'primary.main' : 'text.secondary',
                    fontWeight: serviceTab === 'internet' ? 'bold' : 'normal',
                  }}
                  onClick={() => setServiceTab('internet')}
                >
                  Internet Services ({getFilteredServiceCount(customerServices.internet, 'internet')})
                </Button>
                <Button
                  sx={{
                    borderBottom: serviceTab === 'addons' ? 2 : 0,
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    color: serviceTab === 'addons' ? 'primary.main' : 'text.secondary',
                    fontWeight: serviceTab === 'addons' ? 'bold' : 'normal',
                  }}
                  onClick={() => setServiceTab('addons')}
                >
                  Add-ons ({getFilteredServiceCount(customerServices.addons, 'addons')})
                </Button>
              </Box>

              {/* Phone Services Tab */}
              {serviceTab === 'phone' && (
                <Box>
                  {customerServices.phone?.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 3 }}>No phone services assigned</Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Package Name</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell align="right">Monthly Price</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerServices.phone
                            ?.filter((pkg: any) => {
                              if (serviceStatusFilter === 'active') return pkg.active && (!pkg.endDate || new Date(pkg.endDate) > new Date());
                              if (serviceStatusFilter === 'cancelled') return !pkg.active || (pkg.endDate && new Date(pkg.endDate) <= new Date());
                              return true; // 'all'
                            })
                            ?.map((pkg: any) => (
                            <TableRow key={pkg.id}>
                              <TableCell>
                                {pkg.packageName}
                                {pkg.instanceId > 1 && (
                                  <Chip label={`Instance ${pkg.instanceId}`} size="small" sx={{ ml: 1 }} />
                                )}
                              </TableCell>
                              <TableCell>{pkg.locationName || '-'}</TableCell>
                              <TableCell align="right">${pkg.monthlyPrice.toFixed(2)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={pkg.active ? 'Active' : 'Inactive'} 
                                  size="small" 
                                  color={pkg.active ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                {pkg.startDate ? new Date(pkg.startDate).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell>
                                {pkg.endDate ? (
                                  <Chip 
                                    label={new Date(pkg.endDate).toLocaleDateString()}
                                    size="small"
                                    color="warning"
                                  />
                                ) : (
                                  <Chip 
                                    label="Active"
                                    size="small"
                                    color="success"
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => handleEditPackage(pkg)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <Tooltip title="Set End Date">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleEndPackage(pkg)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Internet Services Tab */}
              {serviceTab === 'internet' && (
                <Box>
                  {customerServices.internet?.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 3 }}>No internet services assigned</Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Bandwidth</TableCell>
                            <TableCell align="right">Monthly Rate</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Contract End</TableCell>
                            <TableCell>Billing End</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerServices.internet
                            ?.filter((service: any) => {
                              if (serviceStatusFilter === 'active') return service.status === 'active';
                              if (serviceStatusFilter === 'cancelled') return service.status !== 'active';
                              return true; // 'all'
                            })
                            ?.map((service: any) => (
                            <TableRow key={service.id}>
                              <TableCell>
                                {service.isDualInternet ? service.serviceName : `${service.vendorName} - ${service.serviceName}`}
                              </TableCell>
                              <TableCell>{service.locationName || '-'}</TableCell>
                              <TableCell>
                                {service.bandwidthDown || '-'}/{service.bandwidthUp || '-'}
                              </TableCell>
                              <TableCell align="right">${service.monthlyRate.toFixed(2)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={service.status} 
                                  size="small" 
                                  color={service.status === 'active' ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                {service.contractEndDate ? new Date(service.contractEndDate).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell>
                                {service.billingEndDate ? (
                                  <Chip 
                                    label={new Date(service.billingEndDate).toLocaleDateString()}
                                    size="small"
                                    color="warning"
                                  />
                                ) : '-'}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton size="small" color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <Tooltip title="Cancel Service">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleCancelInternetService(service)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Add-ons Tab */}
              {serviceTab === 'addons' && (
                <Box>
                  {customerServices.addons?.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 3 }}>No add-on services assigned</Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Add-on Name</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell>Billing</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerServices.addons
                            ?.filter((addon: any) => {
                              if (serviceStatusFilter === 'active') return addon.status === 'active';
                              if (serviceStatusFilter === 'cancelled') return addon.status !== 'active';
                              return true; // 'all'
                            })
                            ?.map((addon: any) => (
                            <TableRow key={addon.id}>
                              <TableCell>{addon.addonName}</TableCell>
                              <TableCell>{addon.addonCategory}</TableCell>
                              <TableCell align="center">{addon.quantity}</TableCell>
                              <TableCell align="right">${addon.unitPrice.toFixed(2)}</TableCell>
                              <TableCell align="right">${addon.totalPrice.toFixed(2)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={addon.billingCycle} 
                                  size="small" 
                                  color={addon.billingCycle === 'monthly' ? 'primary' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton size="small" color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <Tooltip title="Remove Add-on">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleRemoveAddon(addon)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Add/Edit Package Dialog */}
      <Dialog open={addPackageDialog} onClose={() => {
        setAddPackageDialog(false);
        setEditingPackage(null);
        setPackageType('');
        setPackageCategory('');
        setAddPackageStep(1);
        setSelectedServices({ pbx: null, internet: null, dualInternet: null, addons: [], location: 'main' });
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingPackage ? 'Edit Package' : 'Add Services'} - {selectedCustomer?.companyName || selectedCustomer?.email}
            </Typography>
            <Chip 
              label={`Step ${addPackageStep} of 3`} 
              color="primary" 
              size="small" 
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minHeight: 400 }}>
            {/* Step 1: Category Selection */}
            {addPackageStep === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  What would you like to add?
                </Typography>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                  Select a package category to get started:
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        border: packageCategory === 'residential' ? '2px solid' : '1px solid',
                        borderColor: packageCategory === 'residential' ? 'info.main' : 'divider',
                        bgcolor: packageCategory === 'residential' ? 'action.hover' : 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': { 
                          borderColor: 'info.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => {
                        setPackageCategory('residential');
                        setAddPackageStep(2);
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <PeopleIcon 
                          sx={{ 
                            fontSize: 56, 
                            color: packageCategory === 'residential' ? 'info.main' : 'action.active',
                            mb: 2 
                          }} 
                        />
                        <Typography variant="h5" gutterBottom>
                          Residential
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          Home and small office packages with basic features
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        border: packageCategory === 'business' ? '2px solid' : '1px solid',
                        borderColor: packageCategory === 'business' ? 'warning.main' : 'divider',
                        bgcolor: packageCategory === 'business' ? 'action.hover' : 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': { 
                          borderColor: 'warning.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => {
                        setPackageCategory('business');
                        setAddPackageStep(2);
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <BusinessIcon 
                          sx={{ 
                            fontSize: 56, 
                            color: packageCategory === 'business' ? 'warning.main' : 'action.active',
                            mb: 2 
                          }} 
                        />
                        <Typography variant="h5" gutterBottom>
                          Business
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          Enterprise packages with advanced features and support
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* Step 2: Service Selection */}
            {addPackageStep === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                  Select Services (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Choose phone or internet services, or skip directly to add-ons
                </Typography>
                
                {/* Location/Site Selection */}
                <Card variant="outlined" sx={{ mb: 3, bgcolor: 'info.light', p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Service Location</InputLabel>
                        <Select
                          value={selectedServices.location || 'main'}
                          onChange={(e) => setSelectedServices({ ...selectedServices, location: e.target.value })}
                          label="Service Location"
                        >
                          {customerLocations.map((loc) => (
                            <MenuItem key={loc} value={loc}>
                              {loc === 'main' ? 'Main Location' : loc}
                            </MenuItem>
                          ))}
                          <MenuItem value="new">
                            <em>Add New Location...</em>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Show warning about single PBX package policy */}
                    {selectedServices.pbx && customerServices.phone?.some((p: any) => p.active && (!p.endDate || new Date(p.endDate) > new Date())) && (
                      <Grid item xs={12}>
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <strong>Important:</strong> Customers can only have one active phone package. The existing package will be replaced with the new selection.
                        </Alert>
                      </Grid>
                    )}
                    {selectedServices.location === 'new' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="New Location Name"
                          placeholder="e.g., Branch Office Toronto"
                          onChange={(e) => setSelectedServices({ ...selectedServices, newLocationName: e.target.value })}
                          helperText="Enter a descriptive name for this location"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Card>
                
                {/* PBX Service */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <Box display="flex" alignItems="center">
                          <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="h6">Phone Service</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Phone system with extensions and features
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Phone Package</InputLabel>
                          <Select
                            value={selectedServices.pbx?.id || selectedServices.pbx?.PackageID || ''}
                            onChange={(e) => {
                              const selected = packages.find(p => 
                                (p.id || p.PackageID) === (typeof e.target.value === 'string' ? parseInt(e.target.value) : e.target.value)
                              );
                              setSelectedServices({ ...selectedServices, pbx: selected });
                            }}
                            label="Select Phone Package"
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {packages.map((pkg) => (
                              <MenuItem key={pkg.id || pkg.PackageID} value={pkg.id || pkg.PackageID}>
                                {pkg.name || pkg.PackageName} - ${(pkg.monthlyPrice || pkg.MonthlyPrice || 0).toFixed(2)}/mo
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    
                    {/* Show current active packages warning */}
                    {customerServices.phone?.filter((p: any) => p.active && !p.endDate).length > 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Warning:</strong> This customer already has active phone packages:
                        </Typography>
                        {customerServices.phone
                          .filter((p: any) => p.active && !p.endDate)
                          .map((p: any) => (
                            <Typography key={p.id} variant="body2">
                              • {p.packageName} {p.locationName ? `(${p.locationName})` : ''} - ${p.monthlyPrice}/mo
                            </Typography>
                          ))}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Note:</strong> Adding a new phone package at the same location will automatically end the existing package.
                        </Typography>
                      </Alert>
                    )}
                    
                    {/* Location field for phone packages */}
                    {selectedServices.pbx && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Location Name (Optional)"
                          placeholder="e.g., Main Office, Branch 1, etc."
                          value={selectedServices.pbx.locationName || ''}
                          onChange={(e) => {
                            setSelectedServices({
                              ...selectedServices,
                              pbx: { ...selectedServices.pbx, locationName: e.target.value }
                            });
                          }}
                          helperText="Use locations to manage multiple packages for different sites"
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
                
                {/* Internet Service */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <Box display="flex" alignItems="center">
                          <RouterIcon sx={{ mr: 2, color: 'secondary.main' }} />
                          <Box>
                            <Typography variant="h6">Internet Service</Typography>
                            <Typography variant="body2" color="text.secondary">
                              High-speed internet connectivity
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Internet Package</InputLabel>
                          <Select
                            value={selectedServices.internet?.id || ''}
                            onChange={(e) => {
                              const selected = internetServices.find(s => s.id === parseInt(e.target.value));
                              setSelectedServices({ ...selectedServices, internet: selected });
                            }}
                            label="Select Internet Package"
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {(() => {
                              logger.debug('Internet services in dropdown:', internetServices);
                              return internetServices.map((service) => (
                                <MenuItem key={service.id} value={service.id}>
                                  {service.display_name} - ${service.monthly_price}/mo 
                                  {service.download_speed && service.upload_speed && (
                                    <span> ({service.download_speed}/{service.upload_speed})</span>
                                  )}
                                </MenuItem>
                              ));
                            })()}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                
                {/* Managed Dual Internet Services */}
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WifiIcon />
                      Managed Dual Internet Services
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      High availability internet with Cable + DSL/VDSL/Fibre redundancy
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Dual Internet Package</InputLabel>
                          <Select
                            value={selectedServices.dualInternet?.id || ''}
                            onChange={(e) => {
                              const selected = dualInternetServices.find(s => s.id === parseInt(e.target.value));
                              setSelectedServices({ ...selectedServices, dualInternet: selected });
                            }}
                            label="Select Dual Internet Package"
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {dualInternetServices.map((service) => (
                              <MenuItem key={service.id} value={service.id}>
                                <Box>
                                  <Typography variant="body2">
                                    {service.display_name} - ${service.monthly_price}/mo
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {service.primary_download}/{service.primary_upload} + {service.secondary_download}/{service.secondary_upload}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {selectedServices.dualInternet && (
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                            <Typography variant="caption" color="success.dark">
                              Save {selectedServices.dualInternet.discount_percentage}% with bundling
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
                
                {/* Add-on Services */}
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddIcon />
                      Add-on Services
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Enhance your services with additional features
                    </Typography>
                    
                    {addonServices.length === 0 ? (
                      <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        No add-on services available
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {Object.entries(
                          addonServices.reduce((acc: any, addon: any) => {
                            const category = addon.category || 'Other';
                            if (!acc[category]) acc[category] = [];
                            acc[category].push(addon);
                            return acc;
                          }, {})
                        ).map(([category, categoryAddons]: [string, any]) => (
                          <Grid item xs={12} key={category}>
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 'medium' }}>
                              {category.charAt(0).toUpperCase() + category.slice(1)}
                            </Typography>
                            <Grid container spacing={1}>
                              {categoryAddons.map((addon: any) => {
                                const isSelected = selectedServices.addons.some(a => a.id === addon.id);
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={addon.id}>
                                    <Card 
                                      variant="outlined" 
                                      sx={{ 
                                        cursor: 'pointer',
                                        border: isSelected ? 2 : 1,
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                        '&:hover': {
                                          borderColor: 'primary.main',
                                          bgcolor: 'action.hover'
                                        }
                                      }}
                                      onClick={() => {
                                        if (isSelected) {
                                          setSelectedServices({ 
                                            ...selectedServices, 
                                            addons: selectedServices.addons.filter(a => a.id !== addon.id)
                                          });
                                        } else {
                                          setSelectedServices({ 
                                            ...selectedServices, 
                                            addons: [...selectedServices.addons, { ...addon, quantity: 1 }]
                                          });
                                        }
                                      }}
                                    >
                                      <CardContent sx={{ p: 2 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="start">
                                          <Box flex={1}>
                                            <Typography variant="subtitle2" gutterBottom>
                                              {addon.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                                              {addon.description}
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                              ${addon.price}
                                              <Typography variant="caption" color="text.secondary">
                                                /{addon.billingCycle}
                                              </Typography>
                                            </Typography>
                                            {addon.setupFee > 0 && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Setup fee: ${addon.setupFee}
                                              </Typography>
                                            )}
                                          </Box>
                                          <Checkbox
                                            size="small"
                                            checked={isSelected}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </Box>
                                        {isSelected && addon.chargeType === 'quantity' && (
                                          <Box mt={1}>
                                            <TextField
                                              fullWidth
                                              size="small"
                                              type="number"
                                              label="Quantity"
                                              value={selectedServices.addons.find(a => a.id === addon.id)?.quantity || 1}
                                              InputProps={{ 
                                                inputProps: { min: 1 },
                                                endAdornment: addon.category === 'storage' ? 
                                                  <InputAdornment position="end">GB</InputAdornment> : 
                                                  null
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                              onChange={(e) => {
                                                const quantity = parseInt(e.target.value) || 1;
                                                setSelectedServices({ 
                                                  ...selectedServices, 
                                                  addons: selectedServices.addons.map(a => 
                                                    a.id === addon.id ? { ...a, quantity } : a
                                                  )
                                                });
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
                
                {/* Selected Services Summary */}
                {(selectedServices.pbx || selectedServices.internet || selectedServices.dualInternet || selectedServices.addons.length > 0) && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Selected Services:</Typography>
                    {selectedServices.pbx && (
                      <Chip 
                        label={`PBX: ${selectedServices.pbx.name || selectedServices.pbx.PackageName}`} 
                        color="primary" 
                        size="small" 
                        sx={{ mr: 1, mb: 1 }}
                        onDelete={() => setSelectedServices({ ...selectedServices, pbx: null })}
                      />
                    )}
                    {selectedServices.internet && (
                      <Chip 
                        label={`Internet: ${selectedServices.internet.display_name}`} 
                        color="secondary" 
                        size="small" 
                        sx={{ mr: 1, mb: 1 }}
                        onDelete={() => setSelectedServices({ ...selectedServices, internet: null })}
                      />
                    )}
                    {selectedServices.dualInternet && (
                      <Chip 
                        label={`Dual Internet: ${selectedServices.dualInternet.display_name}`} 
                        color="success" 
                        size="small" 
                        sx={{ mr: 1, mb: 1 }}
                        onDelete={() => setSelectedServices({ ...selectedServices, dualInternet: null })}
                      />
                    )}
                    {selectedServices.addons.map((addon) => (
                      <Chip 
                        key={addon.id}
                        label={`${addon.name} ${addon.quantity > 1 ? `(×${addon.quantity})` : ''}`} 
                        color="default" 
                        size="small" 
                        sx={{ mr: 1, mb: 1 }}
                        onDelete={() => setSelectedServices({ 
                          ...selectedServices, 
                          addons: selectedServices.addons.filter(a => a.id !== addon.id)
                        })}
                      />
                    ))}
                  </Box>
                )}
                
                {/* Order Summary */}
                {(selectedServices.pbx || selectedServices.internet || selectedServices.dualInternet || selectedServices.addons.length > 0) && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom>Order Summary</Typography>
                    {selectedServices.pbx && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>PBX: {selectedServices.pbx.name || selectedServices.pbx.PackageName}</Typography>
                        <Typography fontWeight="bold">${(selectedServices.pbx.monthlyPrice || selectedServices.pbx.MonthlyPrice || 0).toFixed(2)}/mo</Typography>
                      </Box>
                    )}
                    {selectedServices.internet && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Internet: {selectedServices.internet.display_name}</Typography>
                        <Typography fontWeight="bold">${selectedServices.internet.monthly_price}/mo</Typography>
                      </Box>
                    )}
                    {selectedServices.dualInternet && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Dual Internet: {selectedServices.dualInternet.display_name}</Typography>
                        <Typography fontWeight="bold">${selectedServices.dualInternet.monthly_price}/mo</Typography>
                      </Box>
                    )}
                    {selectedServices.addons.map((addon) => (
                      <Box key={addon.id} display="flex" justifyContent="space-between" mb={1}>
                        <Typography>
                          {addon.name}
                          {addon.quantity > 1 && ` (×${addon.quantity})`}
                        </Typography>
                        <Typography fontWeight="bold">
                          ${(addon.price * (addon.quantity || 1)).toFixed(2)}/{addon.billingCycle}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6">Total Monthly</Typography>
                      <Typography variant="h6" color="primary">
                        ${(
                          (selectedServices.pbx?.monthlyPrice || selectedServices.pbx?.MonthlyPrice || 0) + 
                          (selectedServices.internet?.monthly_price || 0) +
                          (selectedServices.dualInternet?.monthly_price || 0) +
                          selectedServices.addons.reduce((sum, addon) => {
                            // Only include monthly add-ons in the monthly total
                            if (addon.billingCycle === 'monthly') {
                              return sum + (addon.price * (addon.quantity || 1));
                            }
                            return sum;
                          }, 0)
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            
          </Box>
        </DialogContent>
        <DialogActions>
          {addPackageStep > 1 && (
            <Button 
              onClick={() => {
                setAddPackageStep(addPackageStep - 1);
                // Clear category when going back to step 1
                if (addPackageStep === 2) {
                  setPackageCategory('');
                }
              }}
              startIcon={<EditIcon />}
            >
              Back
            </Button>
          )}
          <Box flexGrow={1} />
          <Button onClick={() => {
            setAddPackageDialog(false);
            setEditingPackage(null);
            setPackageType('');
            setPackageCategory('');
            setAddPackageStep(1);
            setSelectedServices({ pbx: null, internet: null, dualInternet: null, addons: [], location: 'main' });
          }}>Cancel</Button>
          
          
          {addPackageStep === 2 && (
            <Button 
              onClick={async () => {
                if (!selectedCustomer) return;
                
                // Check if we need to confirm package replacement
                if (selectedServices.pbx && !packageReplaceConfirm) {
                  const locationName = selectedServices.pbx.locationName || '';
                  const existingAtLocation = customerServices.phone?.filter((p: any) => 
                    p.active && 
                    !p.endDate && 
                    (locationName ? p.locationName === locationName : !p.locationName)
                  );
                  
                  if (existingAtLocation?.length > 0) {
                    const confirmMessage = `This will replace the following active package${existingAtLocation.length > 1 ? 's' : ''}:\n\n` +
                      existingAtLocation.map((p: any) => `• ${p.packageName} - $${p.monthlyPrice}/mo`).join('\n') +
                      '\n\nDo you want to continue?';
                    
                    if (window.confirm(confirmMessage)) {
                      setPackageReplaceConfirm(true);
                      // Trigger the button click again with confirmation
                      return;
                    } else {
                      return;
                    }
                  }
                }
                
                // Add all selected services
                try {
                  let hasErrors = false;
                  
                  // Add phone service (always replace existing)
                  if (selectedServices.pbx) {
                    const pbxId = selectedServices.pbx.id || selectedServices.pbx.PackageID;
                    if (pbxId) {
                      try {
                        await api.post(`/endpoints/customer-services/${selectedCustomer.id}/phone`, {
                          packageId: pbxId,
                          locationName: selectedServices.location !== 'main' ? selectedServices.location : null,
                          action: 'replace', // Always replace existing PBX package
                          customPrice: selectedServices.pbx.customPrice || null,
                          notes: selectedServices.pbx.notes || null
                        });
                      } catch (error) {
                        logger.error('Error adding phone service:', error);
                        hasErrors = true;
                      }
                    }
                  }
                  
                  // Add internet service
                  if (selectedServices.internet) {
                    try {
                      await api.post(`/endpoints/customer-services/${selectedCustomer.id}/internet`, {
                        serviceId: selectedServices.internet.id,
                        vendorId: 1, // Default vendor ID
                        locationName: selectedServices.location !== 'main' ? selectedServices.location : null,
                        serviceAddress: selectedServices.internet.serviceAddress || null,
                        monthlyRate: selectedServices.internet.monthly_price,
                        setupFee: selectedServices.internet.setup_fee || 0,
                        bandwidthUp: selectedServices.internet.upload_speed,
                        bandwidthDown: selectedServices.internet.download_speed,
                        serviceType: selectedServices.internet.service_type,
                        notes: selectedServices.internet.notes || null
                      });
                    } catch (error) {
                      logger.error('Error adding internet service:', error);
                      hasErrors = true;
                    }
                  }
                  
                  // Add dual internet service
                  if (selectedServices.dualInternet) {
                    try {
                      await api.post(`/endpoints/customer-services/${selectedCustomer.id}/dual-internet`, {
                        dualServiceId: selectedServices.dualInternet.id,
                        primaryVendorId: selectedServices.dualInternet.primary_vendor_id || 2, // Default to Rogers
                        secondaryVendorId: selectedServices.dualInternet.secondary_vendor_id || 6, // Default to Fibernetics
                        locationName: selectedServices.location !== 'main' ? selectedServices.location : null,
                        primaryAddress: selectedServices.dualInternet.primaryAddress || null,
                        secondaryAddress: selectedServices.dualInternet.secondaryAddress || null,
                        monthlyRate: selectedServices.dualInternet.monthly_price,
                        setupFee: selectedServices.dualInternet.setup_fee || 100,
                        notes: selectedServices.dualInternet.notes || null
                      });
                    } catch (error) {
                      logger.error('Error adding dual internet service:', error);
                      hasErrors = true;
                    }
                  }
                  
                  // Add selected add-ons
                  logger.debug('Selected addons to save:', selectedServices.addons);
                  for (const addon of selectedServices.addons) {
                    try {
                      logger.debug('Saving addon:', addon);
                      const addonResponse = await api.post(`/endpoints/customer-services/${selectedCustomer.id}/addons`, {
                        addonId: addon.id,
                        quantity: addon.quantity || 1,
                        parentType: null,  // Standalone addon - no parent required
                        parentId: null,
                        notes: addon.notes || null
                      });
                      logger.debug('Addon save response:', addonResponse.data);
                    } catch (error) {
                      logger.error('Error adding addon:', error);
                      hasErrors = true;
                    }
                  }
                  
                  if (!hasErrors) {
                    setSnackbar({ 
                      open: true, 
                      message: 'Services added successfully', 
                      severity: 'success' 
                    });
                  } else {
                    setSnackbar({ 
                      open: true, 
                      message: 'Some services failed to add', 
                      severity: 'warning' 
                    });
                  }
                  
                  // Reload customer services
                  handleManagePackages(selectedCustomer);
                  
                  // Reset dialog
                  setAddPackageDialog(false);
                  setEditingPackage(null);
                  setPackageType('');
                  setPackageCategory('');
                  setAddPackageStep(1);
                  setSelectedServices({ pbx: null, internet: null, dualInternet: null, addons: [], location: 'main' });
                  setPackageReplaceConfirm(false);
                  
                  // Reload main customer list to update totals
                  fetchCustomers();
                } catch (error) {
                  logger.error('Error adding services:', error);
                  setSnackbar({ 
                    open: true, 
                    message: 'Failed to add services', 
                    severity: 'error' 
                  });
                }
              }}
              variant="contained" 
              color="primary"
              disabled={!selectedServices.pbx && !selectedServices.internet && !selectedServices.dualInternet && selectedServices.addons.length === 0}
            >
              Add Services
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Edit Customer Package Dialog */}
      <Dialog open={editPackageDialog} onClose={() => {
        setEditPackageDialog(false);
        setEditingPackage(null);
        setEditFormData({});
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Package Settings - {editingPackage?.packageName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Customize package settings for this customer. These changes only apply to this customer's package.
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Price"
                  type="number"
                  value={editFormData.monthlyPrice || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, monthlyPrice: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Setup Fee"
                  type="number"
                  value={editFormData.setupFee || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, setupFee: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={editFormData.notes || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  placeholder="Add any special notes about this package..."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.active}
                      onChange={(e) => setEditFormData({ ...editFormData, active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Package Active"
                />
              </Grid>
            </Grid>
            
            {editingPackage && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Package Details</Typography>
                <Typography variant="body2" color="text.secondary">
                  Base Package: {editingPackage.packageName}<br />
                  Original Price: ${editingPackage.basePrice || editingPackage.monthlyPrice}/month<br />
                  Start Date: {new Date(editingPackage.startDate).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditPackageDialog(false);
            setEditingPackage(null);
            setEditFormData({});
          }}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (!selectedCustomer || !editingPackage) return;
              
              try {
                const response = await api.put(
                  `/endpoints/customers/${selectedCustomer.id}/packages/${editingPackage.id}`,
                  editFormData
                );
                
                if (response.data.success) {
                  setSnackbar({ 
                    open: true, 
                    message: 'Package updated successfully', 
                    severity: 'success' 
                  });
                  // Reload customer packages
                  handleManagePackages(selectedCustomer);
                  setEditPackageDialog(false);
                  setEditingPackage(null);
                  setEditFormData({});
                }
              } catch (error: any) {
                setSnackbar({ 
                  open: true, 
                  message: error.response?.data?.message || 'Failed to update package', 
                  severity: 'error' 
                });
              }
            }}
            variant="contained" 
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Rate Dialog */}
      <Dialog 
        open={customRateDialog} 
        onClose={() => {
          setCustomRateDialog(false);
          setCustomRateForm({
            name: '',
            description: '',
            price: '',
            quantity: '1',
            promoPrice: '',
            promoEndDate: '',
            billDate: new Date().toISOString().split('T')[0],
          });
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {customRateType === 'service' && <PhoneIcon color="primary" />}
            {customRateType === 'internet' && <WifiIcon color="success" />}
            {customRateType === 'addon' && <AddIcon color="warning" />}
            <Typography variant="h6">
              Add {customRateType === 'service' ? 'Service' : customRateType === 'internet' ? 'Internet' : 'Addon'} Rate - {selectedCustomer?.companyName || selectedCustomer?.email}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Name"
                  value={customRateForm.name}
                  onChange={(e) => setCustomRateForm(prev => ({...prev, name: e.target.value}))}
                  placeholder={`Enter ${customRateType} service name`}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={customRateForm.description}
                  onChange={(e) => setCustomRateForm(prev => ({...prev, description: e.target.value}))}
                  placeholder="Optional description"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Monthly Price"
                  type="number"
                  value={customRateForm.price}
                  onChange={(e) => setCustomRateForm(prev => ({...prev, price: e.target.value}))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={customRateForm.quantity}
                  onChange={(e) => setCustomRateForm(prev => ({...prev, quantity: e.target.value}))}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Promo Price (Optional)"
                  type="number"
                  value={customRateForm.promoPrice}
                  onChange={(e) => setCustomRateForm(prev => ({...prev, promoPrice: e.target.value}))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Promo End Date"
                  type="date"
                  value={customRateForm.promoEndDate}
                  onChange={(e) => setCustomRateForm(prev => ({...prev, promoEndDate: e.target.value}))}
                  InputLabelProps={{ shrink: true }}
                  disabled={!customRateForm.promoPrice}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bill Start Date"
                  type="date"
                  value={customRateForm.billDate}
                  onChange={(e) => setCustomRateForm(prev => ({...prev, billDate: e.target.value}))}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomRateDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!selectedCustomer || !customRateForm.name || !customRateForm.price) {
                setSnackbar({ 
                  open: true, 
                  message: 'Please fill in all required fields', 
                  severity: 'error' 
                });
                return;
              }

              try {
                const response = await api.post(`/endpoints/customers/${selectedCustomer.id}/custom-rates`, {
                  name: customRateForm.name,
                  description: customRateForm.description,
                  price: parseFloat(customRateForm.price),
                  quantity: parseInt(customRateForm.quantity),
                  promoPrice: customRateForm.promoPrice ? parseFloat(customRateForm.promoPrice) : null,
                  promoEndDate: customRateForm.promoEndDate || null,
                  billDate: customRateForm.billDate,
                  serviceType: customRateType
                });

                if (response.data.success) {
                  setSnackbar({ 
                    open: true, 
                    message: 'Custom rate added successfully', 
                    severity: 'success' 
                  });
                  // Reload customer packages to show new rate
                  handleManagePackages(selectedCustomer);
                  setCustomRateDialog(false);
                  setCustomRateForm({
                    name: '',
                    description: '',
                    price: '',
                    quantity: '1',
                    promoPrice: '',
                    promoEndDate: '',
                    billDate: new Date().toISOString().split('T')[0],
                  });
                }
              } catch (error: any) {
                setSnackbar({ 
                  open: true, 
                  message: error.response?.data?.message || 'Failed to add custom rate', 
                  severity: 'error' 
                });
              }
            }}
            variant="contained"
            color="primary"
            disabled={!customRateForm.name || !customRateForm.price}
          >
            Add Rate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;